import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import useGradualHide from 'hooks/useGradualHide';
import { useHideOnScrollContext } from 'components/HideOnScrollContext';

/**
 * Optimized HideOnScroll component with smooth, jitter-free animations.
 * Uses RAF + direct DOM manipulation for better performance.
 */
const HideOnScroll = forwardRef(
  (
    {
      children,
      enabled = true,
      onHideTrigger,
      navbarHeight,
      threshold,
      listenElementSelector = null,
    },
    ref,
  ) => {
    const [measuredHeight, setMeasuredHeight] = useState(navbarHeight || 100);
    const childrenRef = useRef();
    const isHiddenRef = useRef(false);

    // Function to measure the height of the children element
    const measureHeight = useCallback(
      () => {
        if (childrenRef.current) {
          const rect = childrenRef.current.getBoundingClientRect();
          const newHeight = rect.height;
          if (newHeight > 0 && newHeight !== measuredHeight) {
            setMeasuredHeight(newHeight);
          }
        }
      },
      [measuredHeight],
    );

    // Use ResizeObserver to detect height changes
    useEffect(
      () => {
        if (!childrenRef.current) {
          return undefined;
        }

        const resizeObserver = new ResizeObserver(() => {
          measureHeight();
        });

        resizeObserver.observe(childrenRef.current);

        // Initial measurement
        measureHeight();

        return () => {
          resizeObserver.disconnect();
        };
      },
      [measureHeight],
    );

    // Use the gradual hide hook
    // Calculate threshold with adaptive scaling based on navbar height
    // Philosophy: "Has user scrolled enough to clearly indicate they want navbar hidden/shown?"
    // - Small navbars (≤80px): 1.2x for slight confirmation buffer
    // - Medium navbars (81-150px): 1.1x for balanced response
    // - Large navbars (>150px): 1.0x to avoid excessive scroll requirements
    const calculateAdaptiveThreshold = height => {
      if (height <= 80) {
        return height * 1.2;
      }
      if (height <= 150) {
        return height * 1.1;
      }
      // For large navbars, snap as soon as fully hidden (no extra buffer needed)
      return height;
    };

    const calculatedThreshold =
      typeof threshold !== 'undefined' ? threshold : calculateAdaptiveThreshold(measuredHeight);

    const { resetVisibility } = useGradualHide({
      navbarHeight: measuredHeight,
      threshold: calculatedThreshold,
      listenElementSelector,
      targetRef: childrenRef,
      enabled,
    });

    const isOnDesktop = useMediaQuery('(min-width: 641px)');
    const context = useHideOnScrollContext();
    const internalRef = useRef();

    // Register this component with the context if available
    useEffect(
      () => {
        if (context && context.registerHideOnScrollRef) {
          const refToRegister = ref || internalRef;
          context.registerHideOnScrollRef(refToRegister);

          return () => {
            if (context.unregisterHideOnScrollRef) {
              context.unregisterHideOnScrollRef(refToRegister);
            }
          };
        }
        return undefined;
      },
      [context, ref],
    );

    useImperativeHandle(ref || internalRef, () => ({
      resetVisibility,
    }));

    // Monitor transform changes to detect hidden state
    useEffect(
      () => {
        if (!childrenRef.current || !onHideTrigger || !enabled || isOnDesktop) {
          return undefined;
        }

        // Use MutationObserver to watch for style changes
        const observer = new MutationObserver(() => {
          if (!childrenRef.current) return;

          const { transform } = childrenRef.current.style;
          const match = transform.match(/translate3d\(0px,\s*(-?\d+(?:\.\d+)?)px,\s*0px\)/);

          if (match) {
            const translateY = parseFloat(match[1]);
            const newIsHidden = translateY <= -measuredHeight * 0.5;

            if (newIsHidden !== isHiddenRef.current) {
              isHiddenRef.current = newIsHidden;
              onHideTrigger(newIsHidden);
            }
          }
        });

        observer.observe(childrenRef.current, {
          attributes: true,
          attributeFilter: ['style'],
        });

        return () => {
          observer.disconnect();
        };
      },
      [onHideTrigger, enabled, isOnDesktop, measuredHeight],
    );

    const isSearchFilters = children.props.className === 'search__filters-mobile-wrapper';

    if (enabled && !isOnDesktop) {
      return React.cloneElement(children, {
        ref: childrenRef,
        style: {
          ...children.props.style,
          // Transform will be applied directly by the hook
          // Add GPU acceleration hint
          willChange: 'transform',
          // For search filters, we might want different behavior
          ...(isSearchFilters && {
            transition: 'transform 0.2s ease-out',
          }),
        },
      });
    }

    // Even when disabled, we still need to measure height
    return React.cloneElement(children, {
      ref: childrenRef,
      style: children.props.style,
    });
  },
);

HideOnScroll.propTypes = {
  children: PropTypes.element.isRequired,
  enabled: PropTypes.bool,
  onHideTrigger: PropTypes.func,
  navbarHeight: PropTypes.number,
  threshold: PropTypes.number,
  listenElementSelector: PropTypes.string,
};

export default HideOnScroll;
